/**
 * SF-4845: Copy environment pages.
 *   /eng/env-copy             start a copy, and the recent copies
 *   /eng/env-copy/runs/:id    one copy, read again every 3 seconds while it runs
 * Both need eng:env-copy, like every sf-api /admin/env-copy route; the server is the real
 * check, the page only avoids showing a doomed form.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePermissions } from '@refinedev/core';
import { Alert, Button, Card, Descriptions, Result, Skeleton, Space, Spin, Typography } from 'antd';
import { hasPermission } from '../../../utils/permissions';
import { ENV_COPY_PATH, ENV_COPY_PERMISSION, ENV_COPY_RESUME_WAIT_MS, envLabel } from './constants';
import {
  envCopyErrorMessage,
  runRefetchInterval,
  useCopyStartScope,
  useEnvCopyConfig,
  useEnvCopyRun,
  type EnvCopyRun,
} from './api/envCopyClient';
import { ResumeCopy } from './components/ResumeCopy';
import { StartCopyForm } from './components/StartCopyForm';
import { EnvCopyRunList, RunStatusTag } from './components/EnvCopyRunList';
import { EnvCopySteps } from './components/EnvCopySteps';
import {
  durationBetween,
  filesOptionsSummary,
  formatTime,
  starterName,
  usageOptionSummary,
} from './format';

const { Title, Text } = Typography;

const Gate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: permissions, isLoading } = usePermissions<string[]>();
  if (isLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }
  if (!hasPermission(permissions ?? [], ENV_COPY_PERMISSION)) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle="Copying environments needs the eng:env-copy permission."
      />
    );
  }
  return <>{children}</>;
};

const StartSection: React.FC = () => {
  const query = useEnvCopyConfig(true);
  const scope = useCopyStartScope();
  const config = query.data?.data;
  if (query.isLoading) {
    return (
      <Card title="Start a copy">
        <Skeleton active paragraph={{ rows: 3 }} />
      </Card>
    );
  }
  if (!config || !Array.isArray(config.sources) || !Array.isArray(config.targets)) {
    return (
      <Card title="Start a copy">
        <Space direction="vertical">
          <Alert
            type="error"
            showIcon
            message={envCopyErrorMessage(query.error, 'Could not load the copy settings.')}
          />
          <Button size="small" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </Space>
      </Card>
    );
  }
  // A refresh that fails keeps the choices on screen but turns Copy off until the settings load.
  const notice = query.isError ? (
    <Alert
      style={{ marginBottom: 16 }}
      type="error"
      showIcon
      message="Could not refresh the copy settings. Copy is off until they load."
      description={envCopyErrorMessage(query.error, '')}
      action={
        <Button size="small" onClick={() => void query.refetch()}>
          Retry
        </Button>
      }
    />
  ) : null;
  return (
    <StartCopyForm
      config={config}
      settingsReady={!query.isFetching && !query.isError}
      notice={notice}
      scope={scope}
    />
  );
};

const EnvCopyHome: React.FC = () => (
  <Space direction="vertical" size="large" style={{ width: '100%' }}>
    <div>
      <Title level={3} style={{ marginBottom: 0 }}>
        Copy environment
      </Title>
      <Text type="secondary">
        Copy production onto stage or dev, or stage and dev onto each other. Production is never
        changed.
      </Text>
    </div>
    <StartSection />
    <EnvCopyRunList />
  </Space>
);

export const EnvCopyPage: React.FC = () => (
  <Gate>
    <EnvCopyHome />
  </Gate>
);

/** Which runner attempt a run last saw: a runner sets both when it claims the run. */
const attemptOf = (run: EnvCopyRun) => `${run.heartbeat_at ?? ''}|${run.executor ?? ''}`;

/** An accepted resume the page is waiting on: the attempt the resume answer showed, and when. */
interface ResumeWait {
  attempt: string;
  since: number;
}

const RunView: React.FC<{ id: string }> = ({ id }) => {
  // Set once a resume is accepted, so a copy still marked "needs resuming" is read again until
  // the runner picks it up. The wait ends when a read shows another status or a newer attempt
  // (one that ran and stopped again between two reads), or after ENV_COPY_RESUME_WAIT_MS.
  const [resumeWait, setResumeWait] = useState<ResumeWait | null>(null);
  const [notStarted, setNotStarted] = useState(false);
  const followRecovery = resumeWait !== null;
  const query = useEnvCopyRun(id, true, followRecovery);
  const answer = query.data;
  // Only the run in the address, and never once the server refused it (a refusal is the data).
  const run = answer?.kind === 'run' && answer.run.run_id === id ? answer.run : undefined;
  const refused = answer?.kind === 'refused' ? answer : undefined;
  const runStatus = run?.status;
  const polling = runRefetchInterval(answer, followRecovery) !== false;
  const now = query.dataUpdatedAt || Date.now();

  const readAt = query.dataUpdatedAt;

  useEffect(() => {
    if (runStatus && runStatus !== 'recovery_required') setNotStarted(false);
  }, [runStatus]);

  useEffect(() => {
    // Only reads from after the resume was accepted count.
    if (!resumeWait || !run || readAt < resumeWait.since) return;
    if (run.status !== 'recovery_required' || attemptOf(run) !== resumeWait.attempt) {
      setResumeWait(null);
    }
  }, [resumeWait, run, readAt]);

  useEffect(() => {
    if (!resumeWait) return undefined;
    const timer = setTimeout(() => {
      setResumeWait(null);
      setNotStarted(true);
    }, ENV_COPY_RESUME_WAIT_MS);
    return () => clearTimeout(timer);
  }, [resumeWait]);

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Link to={ENV_COPY_PATH}>Back to Copy environment</Link>
      <Card
        title={run ? `Copy ${envLabel(run.source)} onto ${envLabel(run.target)}` : 'Copy'}
        extra={run && <RunStatusTag status={run.status} />}
      >
        {query.isLoading && <Spin />}
        {refused && (
          <Space direction="vertical">
            {query.isError && (
              <Alert
                type="warning"
                showIcon
                message="Could not refresh. Showing what was last loaded."
              />
            )}
            <Alert type="error" showIcon message={refused.message} />
          </Space>
        )}
        {query.isError && !answer && (
          <Space direction="vertical">
            <Alert
              type="error"
              showIcon
              message={envCopyErrorMessage(query.error, 'Could not load this copy.')}
            />
            <Button size="small" onClick={() => void query.refetch()}>
              Retry
            </Button>
          </Space>
        )}
        {run && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {query.isError && (
              <Alert
                type="warning"
                showIcon
                message={
                  polling
                    ? 'Could not read the latest progress. Trying again in a few seconds.'
                    : 'Could not refresh. Showing what was last loaded.'
                }
              />
            )}
            {run.status === 'recovery_required' ? (
              <ResumeCopy
                run={run}
                resuming={followRecovery}
                notStarted={notStarted}
                checking={query.isFetching}
                onResumed={(answered) => {
                  setNotStarted(false);
                  setResumeWait({ attempt: attemptOf(answered), since: Date.now() });
                }}
                onCheckAgain={() => void query.refetch()}
              />
            ) : (
              run.error && <Alert type="error" showIcon message={run.error} />
            )}
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Started by">
                {starterName(run.started_by)}
              </Descriptions.Item>
              <Descriptions.Item label="Asked for">{formatTime(run.created_at)}</Descriptions.Item>
              {run.started_at && (
                <Descriptions.Item label="Started">{formatTime(run.started_at)}</Descriptions.Item>
              )}
              {run.ended_at && (
                <Descriptions.Item label="Ended">{formatTime(run.ended_at)}</Descriptions.Item>
              )}
              {run.started_at && (
                <Descriptions.Item label="Time taken">
                  {durationBetween(run.started_at, run.ended_at, now)}
                </Descriptions.Item>
              )}
              {/* A run from before the permissions option has no copyPermissions: it copied them.
                  SF-5114: every new run records deleteExtras true; an older run, resumed or not,
                  removes files only if it was started with deleteExtras. */}
              <Descriptions.Item label="Options">
                {usageOptionSummary(run)}; admin accounts and permissions{' '}
                {run.options?.copyPermissions === false ? 'kept' : 'copied'}; files the source does
                not have {run.options?.deleteExtras ? 'removed' : 'kept'}
              </Descriptions.Item>
              <Descriptions.Item label="Images and report files">
                {filesOptionsSummary(run.options)}
              </Descriptions.Item>
            </Descriptions>
            <EnvCopySteps steps={Array.isArray(run.steps) ? run.steps : []} now={now} />
          </Space>
        )}
      </Card>
    </Space>
  );
};

export const EnvCopyRunPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  // Keyed by id: a different run gets fresh state, never the old run's screen.
  return (
    <Gate>
      <RunView key={id} id={id} />
    </Gate>
  );
};
